import { FRCWidgetCompleteEvent, FRCWidgetWidgetErrorEvent, FriendlyCaptchaSDK } from '@friendlycaptcha/sdk';
import { FriendlyCaptchaFrame } from '@services/utils/friendly-captcha-frame.util';

/** What the mounted captcha reports, and how it is mounted. */
export interface FriendlyCaptchaMountOptions {
  element: HTMLElement;
  sitekey: string;
  /** Two-letter language code, as passed to the SDK. */
  language: string;
  /**
   * Web-component delivery: the integrator page's CSP decides what may be framed, and bank CSPs
   * refuse *.frcapi.com - so the SDK runs inside the Smart Widget's own frame (FriendlyCaptchaFrame).
   */
  webComponent: boolean;
  onComplete: (response: string) => void;
  onError: (error: unknown) => void;
}

/** A mounted captcha; destroy() removes it and its listeners. */
export interface MountedFriendlyCaptcha {
  destroy(): void;
}

/**
 * Mounts a Friendly Captcha that starts solving at once.
 *
 * Used where the customer is being asked for a FRESH answer (the payment step after a refused
 * booking), so there is no form focus to wait for: it starts immediately, in both delivery modes -
 * the same two mounts the personal form uses, with the start made explicit.
 */
export function mountFriendlyCaptcha(options: FriendlyCaptchaMountOptions): MountedFriendlyCaptcha {
  if (options.webComponent) {
    const frame = new FriendlyCaptchaFrame({
      element: options.element,
      sitekey: options.sitekey,
      language: options.language,
      onComplete: options.onComplete,
      onError: options.onError,
    });
    frame.start();

    return { destroy: () => frame.destroy() };
  }

  const handle = new FriendlyCaptchaSDK().createWidget({
    element: options.element,
    sitekey: options.sitekey,
    language: options.language,
    startMode: 'auto',
  });
  handle.addEventListener('frc:widget.complete', (event: FRCWidgetCompleteEvent) => options.onComplete(event.detail.response));
  handle.addEventListener('frc:widget.error', (event: FRCWidgetWidgetErrorEvent) => options.onError(event.detail.error));

  return { destroy: () => handle.destroy() };
}
